import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import type { TestQuestionSource } from '../constants/test-question-source';
import type { TestPeriod } from '../constants/test-period';

export interface OrganizationSettings {
  name: string;
  about: string;
  country: string;
  industry: string;
  testsEnabled: boolean;
  testQuestionSource: TestQuestionSource;
  testQuestionCount: number;
  testMinLevels: number;
  timedTestsEnabled: boolean;
  testTimeLimitSeconds: number;
  testMinPerPeriod: number;
  testMaxPerPeriod: number;
  testPeriod: TestPeriod;
  testLimitsEnabled: boolean;
}

async function fetchOrganizationSettings(
  organizationId: number,
): Promise<OrganizationSettings> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_SETTINGS(organizationId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch organization');
  }

  return response.json();
}

export function useOrganizationSettings(
  organizationId: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'settings'],
    queryFn: () => fetchOrganizationSettings(organizationId),
    enabled,
  });
}

export function useUpdateOrganizationSettings(organizationId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<OrganizationSettings>) => {
      const response = await apiFetch(
        ENDPOINTS.ORGANIZATION_SETTINGS(organizationId),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update settings');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'settings'],
      });
    },
  });
}
